import React, { useEffect, useState } from 'react'

import { useThemeStyles } from '@shared/theme/use-theme-styles'
import { useRouter } from '@shared/hooks/use-router'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { ErrorsAlert } from '@shared/components/errors-alert.component'
import { ProgressButton } from '@shared/components/progress-button.component'
import { FormProvider, useForm } from 'react-hook-form'
import { DropzoneAreaBase, FileObject } from 'react-mui-dropzone'
import makeStyles from '@mui/styles/makeStyles'
import { useMutation, useQueryClient } from 'react-query'
import { apiJSON, NormalizedErrors } from '@shared/utils/api/api.util'
import { useResourceQuery } from '@shared/hooks/use-resource-query.hook'
import capitalizeFirstLetter from '@shared/utils/capitalize-first-letter'

type RouteQuery = {
  resourceID?: string
  companyID?: string
}

export const DecisionsDialog = ({
  companyID,
  onClose,
  afterCreate,
}: {
  companyID?: string
  onClose?: () => void
  afterCreate?: () => void
}) => {
  const queryClient = useQueryClient()
  const classes = useThemeStyles()
  const styles = useStyles()
  const [fileObject, setFileObject] = useState<FileObject | null>(null)
  const [error, setError] = useState<NormalizedErrors | null>(null)
  const { navigate, query } = useRouter<RouteQuery>()
  const isResourceEdit = Boolean(query?.resourceID)

  const formMethods = useForm<any>({
    defaultValues: { Name: '', File: null, CompanyId: companyID },
  })
  const { reset: formMethodsReset } = formMethods

  const { resourceData, readResourceData, isResourceDataFetching } =
    useResourceQuery(
      `companies/${query?.companyID}/Decisions`,
      query?.resourceID
    )

  const {
    mutate: createResource,
    isLoading: isResourceCreating,
    isSuccess: isResourceCreationSucceed,
    error: createResourceErrors,
    reset: resetResourceCreationState,
  } = useMutation<unknown, NormalizedErrors, object>(
    (resourceData: object) =>
      apiJSON(
        `companies/${query.companyID}/Decisions/${isResourceEdit ? `/${query.resourceID || ''}` : ''}`,
        resourceData,
        {
          method: isResourceEdit ? 'PUT' : 'POST',
          headers: new Headers(),
        }
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries()
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes('crud'),
        })
      },
    }
  )

  const handleSubmit = formMethods.handleSubmit((resourceFormData) => {
    setError(null)
    if (!isResourceEdit && !resourceFormData.File) {
      setError(['Plik jest wymagany'])
      return
    }
    resetResourceCreationState()
    const formData = new FormData()
    if (isResourceEdit) {
      formData.append('Id', query?.resourceID || '')
    }

    Object.keys(resourceFormData).forEach((key) => {
      if (key === 'File') {
        formData.append(key, resourceFormData[key].file)
      } else {
        formData.append(
          key,
          key === 'CompanyId' ? +resourceFormData[key] : resourceFormData[key]
        )
      }
    })

    createResource(formData)
  })

  useEffect(() => {
    if (isResourceEdit) {
      readResourceData()
    }
  }, [isResourceEdit, readResourceData])

  useEffect(() => {
    if (isResourceEdit && resourceData) {
      setTimeout(() => {
        formMethodsReset(
          Object.keys(resourceData).reduce(
            (acc: Record<string, any>, key: string) => {
              acc[capitalizeFirstLetter(key)] = resourceData[key]
              return acc
            },
            {}
          )
        )
      }, 10)
    }
  }, [isResourceEdit, resourceData, formMethodsReset])

  useEffect(() => {
    formMethods.setValue('File', fileObject)
  }, [fileObject])

  useEffect(() => {
    if (isResourceCreationSucceed) {
      if (afterCreate) {
        return afterCreate()
      }
      navigate(-1)
    }
  }, [afterCreate, isResourceCreationSucceed])

  return (
    <Dialog fullWidth open={true} maxWidth="md">
      <form noValidate>
        <FormProvider {...formMethods}>
          <DialogTitle className={classes.dialogTitle}>
            {isResourceEdit ? 'Edytuj' : 'Dodaj nową decyzję'}
          </DialogTitle>
          <DialogContent dividers>
            <ErrorsAlert
              errors={createResourceErrors || error}
              onClose={resetResourceCreationState}
            />
            <TextField
              {...formMethods.register('Name', {
                required: true,
              })}
              required={true}
              key="Name"
              label="Wyświetlana nazwa decyzji"
              fullWidth
              multiline={true}
              rows={10}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
            />
            <DropzoneAreaBase
              maxFileSize={10485760} // 10MB in bytes
              dropzoneText="Przeciągnij i upuść decyzję tutaj lub kliknij w ten obszar"
              dropzoneClass={styles.dropzoneClass}
              dropzoneParagraphClass={styles.dropzoneParagraphClass}
              useChipsForPreview
              fileObjects={fileObject ? [fileObject] : []}
              filesLimit={1}
              showPreviews
              showPreviewsInDropzone={false}
              previewText=""
              previewGridClasses={{
                container: styles.previewClass,
              }}
              showFileNamesInPreview
              onAdd={(newFiles) => {
                setFileObject(newFiles[0])
              }}
              onDelete={() => {
                setFileObject(null)
              }}
              getDropRejectMessage={(
                rejectedFile,
                acceptedFiles,
                maxFileSize
              ) => {
                if (rejectedFile.size > maxFileSize) {
                  return `Plik "${rejectedFile.name}" jest za duży. Maksymalny rozmiar pliku to 10MB.`
                }
                return `Plik "${rejectedFile.name}" został odrzucony.`
              }}
            />
            <Typography variant="caption" color="textSecondary">
              Maksymalny rozmiar pliku to 10MB.
              Tylko PDF, DOC, DOCX, XLS, XLSX są dozwolone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => (onClose ? onClose() : navigate(-1))}
              color="primary"
              variant="outlined"
            >
              Anuluj
            </Button>
            <ProgressButton
              onClick={handleSubmit}
              isLoading={isResourceCreating}
              color="primary"
              variant="contained"
            >
              Zapisz
            </ProgressButton>
          </DialogActions>
        </FormProvider>
      </form>
    </Dialog>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    dropzoneClass: {
      padding: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    previewClass: {
      marginTop: '10px',
    },
    dropzoneParagraphClass: {
      ...theme.typography.caption,
    },
  }
})
